import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheckIcon, AlertTriangleIcon } from "lucide-react";

export default function Login() {
  const { login, error, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && user !== false) {
      navigate("/");
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 relative items-end p-12 grid-bg" style={{ backgroundColor: "#0A0A0C" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0C] via-transparent to-[#0A0A0C]/80" />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-12 h-12 bg-[#FFD60A] flex items-center justify-center">
              <ShieldCheckIcon className="w-7 h-7 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-3xl font-bold tracking-wide leading-none">CHEM·WMS</div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mt-1">Real-Time Operations</div>
            </div>
          </div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold leading-none mb-6">
            Controle químico<br />em <span className="text-[#FFD60A]">tempo real</span>.
          </h1>
          <p className="text-muted-foreground text-base mb-8 leading-relaxed">
            Envase, telemetria GPS, logística reversa e auditoria de quebra operacional —
            tudo numa plataforma de comando único.
          </p>
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              { k: "Triplos", v: "Lacres" },
              { k: "FEFO", v: "Ativos" },
              { k: "GPS", v: "Geofencing" },
            ].map((it) => (
              <div key={it.k} className="border border-border p-3 bg-[#14151A]/60">
                <div className="font-display text-2xl font-bold text-[#FFD60A]">{it.k}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{it.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-[#0d0d10]">
        <Card className="w-full max-w-md bg-[#14151A] border-border rounded-none p-8">
          <div className="mb-8">
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] mb-2">Acesso restrito</div>
            <h2 className="font-display text-3xl font-bold">Identifique-se</h2>
            <p className="text-sm text-muted-foreground mt-1">Use suas credenciais corporativas.</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">E-mail ou Usuário</Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="mt-2 rounded-none bg-[#0A0A0C] border-border focus-visible:ring-[#00E5FF] focus-visible:ring-offset-0 h-12 font-mono text-sm"
                placeholder="operador@wms.com ou usuário do fornecedor"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="mt-2 rounded-none bg-[#0A0A0C] border-border focus-visible:ring-[#00E5FF] focus-visible:ring-offset-0 h-12 font-mono text-sm"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 text-sm text-[#FF3B30] bg-[#FF3B30]/10 border border-[#FF3B30]/30 px-3 py-2">
                <AlertTriangleIcon className="w-4 h-4" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 bg-[#FFD60A] text-black hover:bg-[#ffdf3a] rounded-none font-bold uppercase tracking-wider"
            >
              {loading ? "Autenticando..." : "Entrar"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
